import React, { useState } from 'react';
import { Box, Grid, Typography, Button, Alert, CircularProgress } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MapIcon from '@mui/icons-material/Map';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import mrfMacPic from '../../../../assets/news-letter/overall-info.png';
import phoneImg from '../../../../assets/news-letter/phone.webp';
import blogOne from '../../../../assets/news-letter/sec1.jpg';
import blogFive from '../../../../assets/news-letter/sec2.jpg';
import blogTen from '../../../../assets/news-letter/sec3.webp';
import { newsletterSignup } from '../redux/actions';
import circleArrow from '../../../../assets/news-letter/arrow-icon.png';
import MYREDFOLDER from "../../../../assets/MyRedFolderLogo.png";
import reason1 from '../../../../assets/news-letter/reason1.svg';
import reason3 from '../../../../assets/news-letter/reason3.png';
import reason2 from '../../../../assets/news-letter/reason2.webp';
import '../styles/newsletter.css';

const validateEmail = (email) => {
  const reg = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return reg.test(email);
};

const features = [
  {
    icon: <img src={reason1} alt="" className="nl-feature-icon"/>,
    label: 'Emergency Preparedness Guides',
    desc: 'Clear step-by-step guides for wildfires, storms, earthquakes, and other emergencies to help you plan ahead.',
  },
  {
    icon: <img src={reason2} alt="" className="nl-feature-icon"/>,
    label: 'Evacuation & Safety Planning Tools',
    desc: 'Checklists and planning sheets for home, school, and community safety — including awareness resources for fast-moving situations and emergencies.',
  },
  {
    icon: <img src={reason3} alt="" className="nl-feature-icon" />,
    label: 'Family & Pet Readiness Kits',
    desc: 'Printable go-bag lists, contact sheets, and pet-specific preparedness guides for every member of your household.',
  },
];

const audiences = [
  {
    img: blogOne,
    title: 'PARENTS & CAREGIVERS',
    desc: 'Who want simple, practical ways to plan for their children during emergencies.',
  },
  {
    img: blogFive,
    title: 'PET OWNERS & FAMILIES',
    desc: 'Who know pets are part of the family and need a real evacuation plan too.',
  },
  {
    img: blogTen,
    title: 'ANYONE IN HIGH-RISK OR BUSY COMMUNITIES',
    desc: 'Who wants to feel more prepared for wildfires, storms, school emergencies, or sudden evacuations — without the stress or confusion.',
  },
];

/* Reusable field: label-row (label left, error right) + input below */
const FormField = ({ label, name, value, onChange, onKeyDown, error, darkBg, idPrefix = 'nl' }) => (
  <div className="nl-form-field">
    <div className="nl-label-row">
      <label htmlFor={`${idPrefix}-${name}`} className={`nl-label ${darkBg ? 'nl-label-light' : ''}`}>
        {label}<span className="required-asterisk" aria-hidden="true">*</span>
      </label>
      {error && <span id={`${idPrefix}-${name}-error`} className="nl-field-error">{error}</span>}
    </div>
    <input
      id={`${idPrefix}-${name}`}
      type={name === 'emailAddress' ? 'email' : 'text'}
      autoComplete={name === 'emailAddress' ? 'email' : 'name'}
      required
      aria-invalid={Boolean(error)}
      aria-describedby={error ? `${idPrefix}-${name}-error` : undefined}
      name={name}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      className={`nl-input${error ? ' nl-input-error' : ''}`}
    />
  </div>
);

const SIGNUP_TERMS =
  "The free MYREDFOLDER® newsletter is sent once a month by the MYREDFOLDER® team. After you sign up, your first issue arrives with the next monthly send. Every email includes an unsubscribe link.";

const NewsLetter = () => {
  const [heroForm, setHeroForm] = useState({ name: '', emailAddress: '' });
  const [ctaForm, setCtaForm] = useState({ name: '', emailAddress: '' });
  const [heroErrors, setHeroErrors] = useState({});
  const [ctaErrors, setCtaErrors] = useState({});
  const [heroSuccess, setHeroSuccess] = useState('');
  const [ctaSuccess, setCtaSuccess] = useState('');
  const [heroLoading, setHeroLoading] = useState(false);
  const [ctaLoading, setCtaLoading] = useState(false);

  const validate = (formData) => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Please enter your name.';
    if (!formData.emailAddress.trim()) {
      errors.emailAddress = 'Please enter a valid email address.';
    } else if (!validateEmail(formData.emailAddress)) {
      errors.emailAddress = 'Please enter a valid email address.';
    }
    return errors;
  };

  const handleSubmit = async (formData, setForm, setErrors, setSuccess, setLoading) => {
    const errors = validate(formData);
    if (Object.keys(errors).length) { setErrors(errors); return; }
    setErrors({});
    setLoading(true);
    try {
      const res = await newsletterSignup({ name: formData.name, emailId: formData.emailAddress });
      if (res && res.status === 200) {
        setSuccess("Thanks for subscribing! Your first issue will arrive with our next monthly newsletter.");
        setForm({ name: '', emailAddress: '' });
        setTimeout(() => setSuccess(''), 5000);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (form, setForm, setErrors) => (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  return (
    <Grid container className='news-letter-container'>

      {/* ── Section 1: Hero ── */}
      <Grid container>
        <Grid item xs={12} sm={6} className="nl-hero-img-wrapper">
          <img src={MYREDFOLDER} alt="MYREDFOLDER®" className="nl-hero-logo" height="66px" style={{ marginTop: 24 }} />
          <img src={mrfMacPic} alt="" className="nl-hero-img" />
        </Grid>

        <Grid item xs={12} sm={6} className="nl-hero-form-wrapper"
          sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
        >
          <Typography component="h1" className="nl-hero-title font-bebas">
            STAY PREPARED.<br />PROTECT WHAT MATTERS MOST.
          </Typography>
          <Typography className="primary-font-normal-5 nl-hero-subtitle">
            Emergencies don't wait. Wildfires, school safety incidents, storms, and sudden evacuations
            can happen without warning — and in those moments, calm matters more than anything.
          </Typography>

          {/* <Box className="nl-hero-bullets">
            {[
              'Emergency Preparedness Guides',
              'Evacuation & Safety Planning Tools',
              'Family & Pet Readiness Kits',
            ].map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                <CheckIcon sx={{ color: 'rgba(255,255,255,0.9)', mr: 1.5, fontSize: 18, flexShrink: 0 }} />
                <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '14px', lineHeight: 1.5 }}>{item}</Typography>
              </Box>
            ))}
          </Box> */}

          {heroSuccess && <Alert severity="success" sx={{ mb: 2 }}>{heroSuccess}</Alert>}

          <Box className="nl-hero-form-card">
            <FormField
              idPrefix="nl-hero"
              label="Name"
              name="name"
              value={heroForm.name}
              onChange={handleChange(heroForm, setHeroForm, setHeroErrors)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit(heroForm, setHeroForm, setHeroErrors, setHeroSuccess, setHeroLoading)}
              error={heroErrors.name}
            />
            <FormField
              idPrefix="nl-hero"
              label="Email Address"
              name="emailAddress"
              value={heroForm.emailAddress}
              onChange={handleChange(heroForm, setHeroForm, setHeroErrors)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit(heroForm, setHeroForm, setHeroErrors, setHeroSuccess, setHeroLoading)}
              error={heroErrors.emailAddress}
            />
            <Button
              fullWidth
              variant="contained"
              disabled={heroLoading}
              onClick={() => handleSubmit(heroForm, setHeroForm, setHeroErrors, setHeroSuccess, setHeroLoading)}
              className="nl-submit-btn"
            >
              Submit {heroLoading && <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />}
            </Button>
            <Typography className="nl-disclaimer-card">
              {SIGNUP_TERMS}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* ── Section 2: We Help Families ── */}
      <Grid container item xs={12} className="nl-section-white">
        <div className="knowledgeBase-header-container" style={{ width: '100%' }}>
          <Grid container spacing={5} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography className="primary-font-bold8 nl-red-heading font-bebas">
                WE HELP FAMILIES STAY CALM, PREPARED, AND READY TO ACT.
              </Typography>
              <Typography className="primary-font-bold5 nl-subheading-dark">
                Join our free monthly newsletter and get simple, practical emergency
                preparedness tools designed for real life.
              </Typography>

              <Box sx={{ mt: 2, mb: 1 }}>
                {[
                  'Disaster readiness guides',
                  'Evacuation planning tips',
                  'Printable safety checklists',
                  'Pet emergency preparedness resources',
                ].map((item) => (
                  <Box key={item} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <CheckIcon sx={{ color: 'black', mr: 1.5, fontSize: 20 }} />
                    <Typography className="primary-font-normal-5">{item}</Typography>
                  </Box>
                ))}
              </Box>

              <Typography className="primary-font-bold5" sx={{ mt: 2 }}>
                Because protecting your family starts with being prepared.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <img src={phoneImg} alt="The MYREDFOLDER app on a phone." className="nl-phone-img" />
            </Grid>
          </Grid>
        </div>
      </Grid>

      {/* ── Section 3: Everything You Need ── */}
      <Grid container item xs={12} className="nl-section-navy">
        <div className="knowledgeBase-header-container" style={{ width: '100%' }}>
          <Typography className="primary-font-bold8 nl-navy-heading font-bebas">
            EVERYTHING YOU NEED TO STAY PREPARED — DELIVERED MONTHLY
          </Typography>

          <Box className="nl-highlight-bar">
            <Typography className="primary-font-normal-5" sx={{ textAlign: 'center', fontSize: '20px', color: '#9d2425' }}>
              Each month, you'll receive easy-to-use resources that help you prepare without the overwhelm:
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {features.map((f) => (
              <Grid key={f.label} item xs={12} sm={4}>
                <Box className="nl-feature-card">
                  {f.icon}
                  <Typography className="primary-font-bold4 nl-feature-label">{f.label}</Typography>
                  <Typography className="nl-feature-desc">{f.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </div>
      </Grid>

      {/* ── Section 4: Made for Everyday Families ── */}
      <Grid container item xs={12} className="nl-section-dark">
        <div className="knowledgeBase-header-container" style={{ width: '100%' }}>
          <Typography className="primary-font-bold8 nl-section-heading font-bebas">
            MADE FOR EVERYDAY FAMILIES <br/>WHO WANT TO BE READY
          </Typography>

          <Box className="nl-badge">
            <Typography className="primary-font-bold4 nl-badge-text font-bebas">
              THIS NEWSLETTER IS FOR:
            </Typography>
          </Box>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            {audiences.map((a) => (
              <Grid key={a.title} item xs={12} sm={4}>
                <img src={a.img} alt="" className="nl-audience-img" />
                <Box sx={{ pt: 2 }}>
                  <Typography className="primary-font-bold4 nl-audience-title font-bebas">
                    {a.title}
                  </Typography>
                  <Typography className="primary-font-normal-5 nl-audience-desc">
                    {a.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </div>
      </Grid>

      {/* ── Section 5: Bottom CTA ── */}
      <Grid container item xs={12} className="nl-section-light">
        <div className="knowledgeBase-header-container last-signup" style={{ width: '100%' }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography className="primary-font-bold8 nl-red-heading font-bebas" sx={{ mb: '0 !important' }}>
                  Start Building Your Emergency Plan Today
                </Typography>
                <img src={circleArrow} alt="" style={{ width: 60, height: 60 }} />
                {/* <ArrowCircleRightOutlinedIcon sx={{ color: '#9a1f21', fontSize: 60, flexShrink: 0 }} /> */}
              </Box>
              <Box style={{ width: '75%' }}>
                <Typography className="primary-font-normal-5 nl-cta-body">
                Preparedness doesn't happen all at once — it happens one simple step at a time.
              </Typography>
              <Typography className="primary-font-normal-5 nl-cta-body">
                Join our free newsletter and get monthly tools that help you protect your family,
                your home, and your pets.
              </Typography>
              <Typography className="primary-font-normal-5 nl-cta-body">
                No spam. No overwhelm. Just useful preparedness you can actually use.
              </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={7}>
              <Box className="nl-cta-card">
                <Typography className="primary-font-bold8 nl-cta-heading" style={{ letterSpacing: 0 }}>
                  Sign up for the free monthly newsletter
                </Typography>

                {ctaSuccess && <Alert severity="success" sx={{ mb: 2 }}>{ctaSuccess}</Alert>}

                <FormField
                  idPrefix="nl-cta"
                  label="Name"
                  name="name"
                  value={ctaForm.name}
                  onChange={handleChange(ctaForm, setCtaForm, setCtaErrors)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit(ctaForm, setCtaForm, setCtaErrors, setCtaSuccess, setCtaLoading)}
                  error={ctaErrors.name}
                  darkBg
                />
                <FormField
                  idPrefix="nl-cta"
                  label="Email Address"
                  name="emailAddress"
                  value={ctaForm.emailAddress}
                  onChange={handleChange(ctaForm, setCtaForm, setCtaErrors)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit(ctaForm, setCtaForm, setCtaErrors, setCtaSuccess, setCtaLoading)}
                  error={ctaErrors.emailAddress}
                  darkBg
                />

                <Button
                  fullWidth
                  variant="contained"
                  disabled={ctaLoading}
                  onClick={() => handleSubmit(ctaForm, setCtaForm, setCtaErrors, setCtaSuccess, setCtaLoading)}
                  className="nl-cta-btn"
                >
                  Submit {ctaLoading && <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />}
                </Button>
                <Typography className="primary-font-normal-9 nl-disclaimer nl-disclaimer-cta">
                  {SIGNUP_TERMS}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </div>
      </Grid>

    </Grid>
  );
};

export default NewsLetter;
